import { useState, useMemo, useEffect } from "react";
import { useAuth } from "wasp/client/auth";
import { useNavigate, Link } from "react-router-dom";
import { useQuery } from "wasp/client/operations";
import { getTopicTypes, getTopicTypeById, updateTopicType, deleteTopicType } from "wasp/client/operations";
import { Filter, Plus, Upload } from "lucide-react";
import LoadingSpinner from "./layout/LoadingSpinner";
import { EnhancedActionButton } from "../components/ui/enhanced-page-layout";
import { TableFilterLayout } from "../components/ui/table-filter-layout";
import { AdminFlyoutPane } from "./components/AdminFlyoutPane";
import { FlyoutGridLayout, FlyoutGridSection, FlyoutGridRow } from "./components/FlyoutGridLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { toast } from "../hooks/use-toast";
import { BulkUploadDialog } from "./bulk-upload/components/BulkUploadDialog";
import { topicTypeConfig } from "./bulk-upload/configs";
import { useBulkUploadEnabled } from "./hooks/useBulkUploadEnabled";

export default function TopicTypesPage() {
  const { data: user } = useAuth();
  const navigate = useNavigate();

  if (!user) {
    return <div>Loading...</div>;
  }

  const { data: topicTypes = [], isLoading, error, refetch } = useQuery(getTopicTypes);
  const { isEnabled: isBulkUploadEnabled } = useBulkUploadEnabled();

  const [searchTerm, setSearchTerm] = useState("");
  const [labelFilter, setLabelFilter] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [selectedTopicTypeId, setSelectedTopicTypeId] = useState<string | null>(null);
  const [flyoutMode, setFlyoutMode] = useState<'view' | 'edit'>('view');

  // Editable fields
  const [editFormData, setEditFormData] = useState({
    key: '',
    label: '',
    description: '',
    isActive: true
  });

  // Fetch selected topic type details for flyout
  const { data: selectedTopicType, isLoading: loadingTopicType } = useQuery(
    getTopicTypeById,
    { id: selectedTopicTypeId || '' },
    { enabled: !!selectedTopicTypeId, keepPreviousData: true }
  );

  const filteredData = useMemo(() => {
    return (topicTypes as any[]).filter((type: any) => {
      if (searchTerm && !type.key.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }
      if (labelFilter && !type.label.toLowerCase().includes(labelFilter.toLowerCase())) {
        return false;
      }
      return true;
    });
  }, [topicTypes, searchTerm, labelFilter]);

  const hasData = (topicTypes as any[]).length > 0;

  // Initialize edit fields when topic type is selected
  useEffect(() => {
    if (selectedTopicType) {
      const type = selectedTopicType as any;
      setEditFormData({
        key: type.key || '',
        label: type.label || '',
        description: type.description || '',
        isActive: type.isActive ?? true
      });
    }
  }, [selectedTopicType]);

  // Check if any changes have been made
  const topicTypeData = selectedTopicType as any;
  const hasChanges = topicTypeData ? (
    editFormData.key !== (topicTypeData.key || '') ||
    editFormData.label !== (topicTypeData.label || '') ||
    editFormData.description !== (topicTypeData.description || '') ||
    editFormData.isActive !== (topicTypeData.isActive ?? true)
  ) : false;

  const handleTopicTypeClick = (topicTypeId: string) => {
    setSelectedTopicTypeId(topicTypeId);
    setFlyoutMode('view');
  };

  const handleEdit = () => {
    setFlyoutMode('edit');
  };

  const handleSave = async () => {
    if (!topicTypeData) return;

    try {
      await updateTopicType({
        id: topicTypeData.id,
        ...editFormData
      });

      await refetch();
      setFlyoutMode('view');

      toast({
        title: "Success",
        description: "Topic Type updated successfully",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to update topic type",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async () => {
    if (!topicTypeData) return;
    if (confirm('Are you sure you want to delete this topic type? This action cannot be undone.')) {
      try {
        await deleteTopicType({ id: topicTypeData.id });
        setSelectedTopicTypeId(null);
        await refetch();
        toast({
          title: "Success",
          description: "Topic Type deleted successfully",
        });
      } catch (error) {
        toast({
          title: "Error",
          description: 'Failed to delete topic type: ' + (error as Error).message,
          variant: "destructive",
        });
      }
    }
  };

  // Navigation helpers
  const currentTopicTypeIndex = filteredData.findIndex(t => t.id === selectedTopicTypeId) ?? -1;
  const hasPrevious = currentTopicTypeIndex > 0;
  const hasNext = currentTopicTypeIndex >= 0 && currentTopicTypeIndex < filteredData.length - 1;

  const handleNavigatePrevious = () => {
    if (hasPrevious && filteredData) {
      const previousTopicType = filteredData[currentTopicTypeIndex - 1];
      setSelectedTopicTypeId(previousTopicType.id);
    }
  };

  const handleNavigateNext = () => {
    if (hasNext && filteredData) {
      const nextTopicType = filteredData[currentTopicTypeIndex + 1];
      setSelectedTopicTypeId(nextTopicType.id);
    }
  };

  return (
    <>
      <div className="w-full">
        {/* Fixed Header */}
        <div className="fixed top-[var(--header-height)] left-[var(--sidebar-width)] right-0 z-30 bg-white dark:bg-sidebar-active transition-colors border-b border-gray-200 dark:border-gray-700">
          <div className="px-4 py-3">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Topic Categories</h1>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  Define and manage topic categories ({filteredData.length} total)
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowFilters(f => !f)}
                  className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md border transition-colors ${
                    showFilters
                      ? 'bg-blue-50 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-600 dark:text-blue-300'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 dark:bg-transparent dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800'
                  }`}
                >
                  <Filter className="w-4 h-4" />
                  Filter
                </button>
                {isBulkUploadEnabled && (
                  <EnhancedActionButton
                    onClick={() => setShowBulkUpload(true)}
                    icon={Upload}
                    label="Bulk Upload"
                    variant="secondary"
                  />
                )}
                <EnhancedActionButton
                  onClick={() => navigate('/admin/topic-types/new')}
                  icon={Plus}
                  label="Create Type"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Spacer to push content below fixed header */}
        <div className="h-20" />

        {/* Table Header with Filters */}
        {hasData && (
          <TableFilterLayout
            showFilters={showFilters}
            columns={[
              { title: 'Key', span: 2 },
              { title: 'Label', span: 3 },
              { title: 'Description', span: 5 },
              { title: 'Topics', span: 2 }
            ]}
            filters={[
              {
                span: 2,
                content: (
                  <input
                      type="text"
                      placeholder="Search key..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                )
              },
              {
                span: 3,
                content: (
                  <input
                    type="text"
                    placeholder="Filter by label..."
                    value={labelFilter}
                    onChange={(e) => setLabelFilter(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                )
              },
              { span: 5, content: <div className="h-10" /> },
              { span: 2, content: <div className="h-10" /> }
            ]}
          />
        )}

        {/* Content */}
        <div className="w-full">
            {isLoading && <LoadingSpinner />}
            {error && (
              <div className="p-4 text-center text-red-600">
                Error loading topic types: {error.message}
              </div>
            )}

            {!isLoading && !error && filteredData.length === 0 && (
              <div className="p-8 text-center text-gray-500">
                <p className="text-lg font-medium">No topic types found</p>
                <p className="text-sm mt-2">
                  {searchTerm || labelFilter ? "Try adjusting your filters" : "Click 'Create Type' to add your first topic type"}
                </p>
              </div>
            )}

            {filteredData.length > 0 && (
              <>
                {filteredData.map((type: any) => (
                  <div
                    key={type.id}
                    className="px-4 py-2.5 admin-hoverable transition-colors grid grid-cols-12 gap-4 items-center border-b border-gray-200 dark:border-gray-700 cursor-pointer"
                    onClick={() => handleTopicTypeClick(type.id)}
                    title="Click to view details"
                  >
                    <div className="col-span-2 overflow-hidden min-w-0">
                      <span className="text-sm font-medium text-gray-900 dark:text-white truncate block">
                        {type.key}
                      </span>
                    </div>
                    <div className="col-span-3 overflow-hidden min-w-0">
                      <span className="text-sm text-gray-900 dark:text-white truncate block">
                        {type.label}
                      </span>
                    </div>
                    <div className="col-span-5 overflow-hidden min-w-0">
                      <span className="text-sm text-gray-600 dark:text-gray-300 truncate block">
                        {type.description || '-'}
                      </span>
                    </div>
                    <div className="col-span-2 overflow-hidden min-w-0">
                      <span className="text-sm text-gray-600 dark:text-gray-300 truncate block">
                        {(type as any)._count?.topics || 0} topics
                      </span>
                    </div>
                  </div>
                ))}
              </>
            )}
        </div>

        {/* Flyout Pane */}
        <AdminFlyoutPane
          open={!!selectedTopicTypeId}
          onOpenChange={(open) => {
            if (!open) {
              setSelectedTopicTypeId(null);
              setFlyoutMode('view');
            }
          }}
          title={flyoutMode === 'view' ? 'View Topic Type' : 'Edit Topic Type'}
          description={topicTypeData?.key || undefined}
          width="xl"
          mode={flyoutMode}
          onNavigatePrevious={handleNavigatePrevious}
          onNavigateNext={handleNavigateNext}
          hasPrevious={hasPrevious}
          hasNext={hasNext}
          headerActions={
            flyoutMode === 'view' ? (
              <>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleDelete}
                  className="bg-red-600 hover:bg-red-700 text-white w-[110px]"
                >
                  Delete
                </Button>
                <Button onClick={handleEdit} size="sm" className="w-[110px]">
                  Edit
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFlyoutMode('view')}
                  className="w-[110px]"
                >
                  {hasChanges ? 'Cancel' : 'Close'}
                </Button>
                <Button
                  onClick={handleSave}
                  size="sm"
                  className="w-[110px]"
                  disabled={!hasChanges}
                >
                  Save
                </Button>
              </>
            )
          }
        >
          {loadingTopicType && !selectedTopicType ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : topicTypeData ? (
            <>
              <FlyoutGridLayout>
                {/* Basic Information Section */}
                <FlyoutGridSection title="BASIC INFORMATION">
                  <FlyoutGridRow
                    label="Key"
                    value={
                      flyoutMode === 'edit' ? (
                        <Input
                          value={editFormData.key}
                          onChange={(e) => setEditFormData({ ...editFormData, key: e.target.value })}
                          className="h-8 text-sm border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0 -my-1"
                          placeholder="Enter key (e.g., fiqh, aqeedah)"
                        />
                      ) : (
                        <span>{topicTypeData.key}</span>
                      )
                    }
                  />
                  <FlyoutGridRow
                    label="Label"
                    value={
                      flyoutMode === 'edit' ? (
                        <Input
                          value={editFormData.label}
                          onChange={(e) => setEditFormData({ ...editFormData, label: e.target.value })}
                          className="h-8 text-sm border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0 -my-1"
                          placeholder="Enter display label"
                        />
                      ) : (
                        <span>{topicTypeData.label}</span>
                      )
                    }
                  />
                  <FlyoutGridRow
                    label="Description"
                    value={
                      flyoutMode === 'edit' ? (
                        <Textarea
                          value={editFormData.description}
                          onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                          className="text-sm border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0 -my-1 min-h-[60px]"
                          placeholder="Enter description"
                        />
                      ) : (
                        <span>{topicTypeData.description || 'None'}</span>
                      )
                    }
                    isLast
                  />
                </FlyoutGridSection>

                {/* Settings Section */}
                <FlyoutGridSection title="SETTINGS">
                  <FlyoutGridRow
                    label="Active Status"
                    value={
                      flyoutMode === 'edit' ? (
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={editFormData.isActive}
                            onCheckedChange={(checked) => setEditFormData({ ...editFormData, isActive: checked })}
                          />
                          <span className="text-sm">{editFormData.isActive ? 'Active' : 'Inactive'}</span>
                        </div>
                      ) : (
                        <span className={topicTypeData.isActive ? 'text-green-600 font-medium' : 'text-gray-600'}>
                          {topicTypeData.isActive ? 'Active' : 'Inactive'}
                        </span>
                      )
                    }
                    isLast
                  />
                </FlyoutGridSection>

                {/* Statistics Section (View Only) */}
                {flyoutMode === 'view' && (
                  <FlyoutGridSection title="STATISTICS">
                    <FlyoutGridRow
                      label="Topics"
                      value={<span>{topicTypeData._count?.topics || 0}</span>}
                      isLast
                    />
                  </FlyoutGridSection>
                )}
              </FlyoutGridLayout>
            </>
          ) : null}
        </AdminFlyoutPane>
      </div>
      <BulkUploadDialog
        open={showBulkUpload}
        onOpenChange={setShowBulkUpload}
        config={topicTypeConfig}
        onComplete={() => refetch()}
      />
    </>
  );
}



